import React, { useEffect, useRef, useState } from 'react';
import { bool, func, node, number, shape, string } from 'prop-types';
import classNames from 'classnames';

import css from './HorizontalStrip.module.css';

// Pixel slop when comparing scroll position to its bounds. Browsers can
// report `scrollLeft` as a fractional value; treating anything within a
// pixel as "at the edge" prevents disabled-state flicker.
const SCROLL_EDGE_EPSILON = 1;

/**
 * Hook: tracks whether the scroller can be scrolled left/right and whether
 * its content overflows. Recomputes on scroll, on resize, and whenever the
 * item count changes. Effects only — SSR renders the strip with both arrows
 * hidden and the client lights them up after mount.
 */
export const useScrollEdges = (scrollerRef, itemCount) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;

    const recompute = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const maxScroll = scrollWidth - clientWidth;
      setOverflows(maxScroll > SCROLL_EDGE_EPSILON);
      setCanScrollLeft(scrollLeft > SCROLL_EDGE_EPSILON);
      setCanScrollRight(scrollLeft < maxScroll - SCROLL_EDGE_EPSILON);
    };

    recompute();
    el.addEventListener('scroll', recompute, { passive: true });
    window.addEventListener('resize', recompute);
    return () => {
      el.removeEventListener('scroll', recompute);
      window.removeEventListener('resize', recompute);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCount]);

  return { canScrollLeft, canScrollRight, overflows };
};

/**
 * Advance the scroller by a full page of items. Scrolls by a whole multiple
 * of the item "pitch" (item width + gap) measured from the DOM rather than
 * by clientWidth, so a page always lands on an item boundary instead of
 * mid-card. Exported for tests.
 */
export const scrollByPage = (el, direction) => {
  if (!el) return;
  const items = el.children;
  const step = items.length >= 2 ? items[1].offsetLeft - items[0].offsetLeft : 0;
  if (step <= 0) {
    el.scrollBy({ left: el.clientWidth * direction, behavior: 'smooth' });
    return;
  }
  // Whole items visible across the content box = one page (the padding
  // inset is excluded so a peeking partial item never counts).
  const inset = parseFloat(window.getComputedStyle(el).paddingLeft) || 0;
  const contentWidth = el.clientWidth - inset * 2;
  const perPage = Math.max(1, Math.floor((contentWidth + SCROLL_EDGE_EPSILON) / step));
  const currentIndex = Math.round(el.scrollLeft / step);
  const targetIndex = Math.max(0, currentIndex + perPage * direction);
  el.scrollTo({ left: targetIndex * step, behavior: 'smooth' });
};

const ChevronLeft = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" />
  </svg>
);
const ChevronRight = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/**
 * HorizontalStrip — a dependency-free, scroll-snap carousel for card rows.
 *
 * Replaces react-slick on the landing page (categories + listings). The
 * scroller is a native horizontal overflow container: touch and trackpad
 * scroll natively, `scroll-snap` lands every gesture on a whole item, and
 * the prev/next buttons page by a measured item pitch.
 *
 * Wide-viewport behaviour: the strip spans the full section width with a
 * symmetric inset that equals the page container's gutter
 * (`max(24px, (100% - 1440px) / 2 + 24px)`), so `perView` items fit exactly
 * inside the content column while the remaining items overflow to the
 * viewport edge — the "cards run to the edge" look. At or below the
 * container width it is pixel-identical to a padded row.
 *
 * `perView` sets how many items are visible per breakpoint (fractions
 * allowed — 5.5 leaves half a card peeking as a scroll cue):
 *   { base, small (550), medium (768), large (1024), xlarge (1440) }
 * Missing keys fall back to the previous breakpoint.
 *
 * Children are the items; each is wrapped in a snap target. While
 * `inProgress` and there are no children, `placeholderCount` copies of
 * `renderPlaceholder()` render in the same wrappers so the skeleton keeps
 * the real layout.
 */
const HorizontalStrip = props => {
  const {
    className,
    scrollerClassName,
    itemClassName,
    children,
    perView,
    gap,
    arrowTop,
    prevLabel,
    nextLabel,
    inProgress,
    placeholderCount,
    renderPlaceholder,
    header,
    headerActions,
  } = props;

  const scrollerRef = useRef(null);
  const items = React.Children.toArray(children).filter(Boolean);
  const showPlaceholders = inProgress && items.length === 0 && renderPlaceholder;
  const rendered = showPlaceholders
    ? Array.from({ length: placeholderCount }).map((_, i) => (
        <React.Fragment key={`placeholder-${i}`}>{renderPlaceholder(i)}</React.Fragment>
      ))
    : items;

  const { canScrollLeft, canScrollRight, overflows } = useScrollEdges(scrollerRef, rendered.length);
  const showArrows = !showPlaceholders && overflows;

  // Every breakpoint gets a value (falling back to the previous one), so the CSS reads one
  // plain variable per breakpoint: nested var() fallbacks inside calc() break the minifier.
  const pv = perView || {};
  const base = pv.base != null ? pv.base : 1.2;
  const small = pv.small != null ? pv.small : base;
  const medium = pv.medium != null ? pv.medium : small;
  const large = pv.large != null ? pv.large : medium;
  const xlarge = pv.xlarge != null ? pv.xlarge : large;
  const scrollerStyle = {
    '--stripGap': `${gap}px`,
    '--perViewBase': base,
    '--perViewSmall': small,
    '--perViewMedium': medium,
    '--perViewLarge': large,
    '--perViewXLarge': xlarge,
  };

  const prevButton = arrowClass => (
    <button
      type="button"
      className={arrowClass}
      onClick={() => scrollByPage(scrollerRef.current, -1)}
      disabled={!canScrollLeft}
      aria-label={prevLabel}
    >
      <ChevronLeft />
    </button>
  );
  const nextButton = arrowClass => (
    <button
      type="button"
      className={arrowClass}
      onClick={() => scrollByPage(scrollerRef.current, 1)}
      disabled={!canScrollRight}
      aria-label={nextLabel}
    >
      <ChevronRight />
    </button>
  );

  // With a `header`, the arrows sit in the header row (right side, after any
  // `headerActions`), lined up with the page column; without one they float over the strip.
  const hasHeader = !!header;

  return (
    <div className={classNames(css.root, className)}>
      {hasHeader ? (
        <div className={css.header}>
          <div className={css.headerMain}>{header}</div>
          <div className={css.headerActions}>
            {headerActions}
            {showArrows ? (
              <div className={css.headerArrows}>
                {prevButton(css.headerArrow)}
                {nextButton(css.headerArrow)}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className={classNames(css.scroller, scrollerClassName)} ref={scrollerRef} style={scrollerStyle}>
        {rendered.map((child, i) => (
          <div key={child.key != null ? child.key : i} className={classNames(css.item, itemClassName)}>
            {child}
          </div>
        ))}
      </div>

      {showArrows && !hasHeader ? (
        <>
          {React.cloneElement(prevButton(classNames(css.arrow, css.arrowPrev)), {
            style: { top: arrowTop },
          })}
          {React.cloneElement(nextButton(classNames(css.arrow, css.arrowNext)), {
            style: { top: arrowTop },
          })}
        </>
      ) : null}
    </div>
  );
};

HorizontalStrip.defaultProps = {
  className: null,
  scrollerClassName: null,
  itemClassName: null,
  perView: { base: 1.2, small: 2, medium: 3, large: 4 },
  gap: 24,
  arrowTop: '40%',
  inProgress: false,
  placeholderCount: 6,
  renderPlaceholder: null,
  header: null,
  headerActions: null,
};

HorizontalStrip.propTypes = {
  className: string,
  scrollerClassName: string,
  itemClassName: string,
  children: node,
  perView: shape({
    base: number.isRequired,
    small: number,
    medium: number,
    large: number,
    xlarge: number,
  }),
  gap: number,
  arrowTop: string,
  // The arrows' names for screen readers (en.json)
  prevLabel: string.isRequired,
  nextLabel: string.isRequired,
  inProgress: bool,
  placeholderCount: number,
  renderPlaceholder: func,
  header: node,
  headerActions: node,
};

export default HorizontalStrip;
